/** POS amounts are stored in MRO; the active display currency may be MRU (÷10). */

import { kvGet } from '../infrastructure/storage/kvAccess';

export const MRO_PER_MRU = 10;

/** Central FR/AR labels for print + UI (single source of truth). */
export const CURRENCY_LABELS = {
  MRO: {
    fr: { short: 'MRO', name: 'MRO' },
    ar: { short: 'أ.ق.', name: 'أوقية قديمة' }
  },
  MRU: {
    fr: { short: 'MRU', name: 'MRU' },
    ar: { short: 'أ.ج.', name: 'أوقية جديدة' }
  }
};

export function normalizeCurrencyType(value) {
  return value === 'MRU' ? 'MRU' : 'MRO';
}

export function normalizeCurrencyLanguage(language) {
  return language === 'ar' ? 'ar' : 'fr';
}

/**
 * Translated currency label (short or full name).
 * @param {'MRO'|'MRU'|string} [currencyType]
 * @param {string} [language]
 * @param {{ form?: 'short'|'name' }} [options]
 */
export function resolveCurrencyLabel(currencyType, language = 'fr', options = {}) {
  const code = normalizeCurrencyType(currencyType);
  const lang = normalizeCurrencyLanguage(language);
  const form = options.form === 'name' ? 'name' : 'short';
  return CURRENCY_LABELS[code][lang][form];
}

export function readActiveCurrencyType() {
  try {
    const v = kvGet('appCurrency');
    return normalizeCurrencyType(v);
  } catch {
    return 'MRO';
  }
}

/** Active app currency label for receipts / invoices (non-React). */
export function getActiveCurrencyLabel(language = 'fr', options = {}) {
  return resolveCurrencyLabel(readActiveCurrencyType(), language, options);
}

export function toDisplayAmount(mroAmount, currencyType = 'MRO') {
  const mro = Number(mroAmount) || 0;
  if (currencyType === 'MRU') {
    return Math.round((mro / MRO_PER_MRU) * 100) / 100;
  }
  return mro;
}

export function toMroAmount(displayAmount, currencyType = 'MRO') {
  const display = Number(displayAmount) || 0;
  if (currencyType === 'MRU') {
    return Math.round(display * MRO_PER_MRU * 100) / 100;
  }
  return display;
}

export function getPayableTotal(cartTotalMro, currencyType = 'MRO') {
  return toDisplayAmount(cartTotalMro, currencyType);
}

/**
 * Validates cash entered in the active display currency against cart total (MRO base).
 * Returns MRO values for persistence (sales, receipts, checkout).
 */
export function validatePosCashPayment(cartTotalMro, cashInput, currencyType = 'MRO') {
  const payable = getPayableTotal(cartTotalMro, currencyType);
  const cash = Number(cashInput) || 0;
  const isValid = cash >= payable;
  const changeDisplay = isValid ? Math.round((cash - payable) * 100) / 100 : 0;
  const shortfall = !isValid ? Math.round((payable - cash) * 100) / 100 : 0;

  return {
    isValid,
    payable,
    cash,
    changeDisplay,
    shortfall,
    cashMro: toMroAmount(cash, currencyType),
    changeMro: toMroAmount(changeDisplay, currencyType)
  };
}

/** Format a value already in the active display currency (e.g. change, cash input). */
export function formatAmountParts(amount, locale = 'fr-MR', currencyLabel = 'MRO') {
  const numeric = Number(amount) || 0;
  const formattedAmount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    numberingSystem: locale.startsWith('ar') ? 'latn' : undefined
  }).format(numeric);

  return { amount: formattedAmount, currency: currencyLabel };
}

/**
 * Stored MRO → display string with active currency label (invoices / payment stats).
 * @param {number|string|null|undefined} amount
 * @param {string} [locale='fr-MR']
 * @returns {string}
 */
export function formatStoredMroAmount(amount, locale = 'fr-MR') {
  const targetCurrency = readActiveCurrencyType();
  const displayAmount = toDisplayAmount(amount, targetCurrency);
  let formatted = new Intl.NumberFormat(locale, {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    numberingSystem: String(locale).startsWith('ar') ? 'latn' : undefined
  }).format(displayAmount);
  formatted = String(formatted).replace(/(\d)\/(\d{3})/g, '$1.$2');
  formatted = String(formatted).replace(/(\d)\/(\d)/g, '$1.$2');
  return `${formatted} ${targetCurrency}`;
}

/** Format a stored MRO price for POS display (product cards, labels). */
export function formatPosPriceParts(mroAmount, currencyType = 'MRO', locale = 'fr-MR', currencyLabel = 'MRO') {
  const display = toDisplayAmount(mroAmount, currencyType);
  return formatAmountParts(display, locale, currencyLabel);
}

/** MRO → display string for purchase modal number inputs. */
export function mroAmountToInputDisplay(mroAmount, currencyType = readActiveCurrencyType()) {
  if (mroAmount === '' || mroAmount == null) return '';
  const n = Number(mroAmount);
  if (!Number.isFinite(n)) return '';
  return String(toDisplayAmount(n, currencyType));
}

/** Display-currency input → MRO (purchase modals, same rules as POS). */
export function parsePurchaseAmountInput(displayAmount, currencyType = readActiveCurrencyType()) {
  if (displayAmount === '' || displayAmount == null) return '';
  const n = parseFloat(displayAmount);
  if (!Number.isFinite(n)) return '';
  return toMroAmount(n, currencyType);
}

/**
 * Parse cash-register / écart amount typed in the active display currency → MRO base.
 * All session totals, écarts, and sales use MRO internally.
 */
export function parseCashRegisterAmountInput(displayValue, currencyType = readActiveCurrencyType()) {
  const numericValue = String(displayValue ?? '')
    .replace(/[^\d.,]/g, '')
    .replace(',', '.');
  const parsed = parseFloat(numericValue);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return toMroAmount(parsed, currencyType);
}

/** Snapshot for écart / login / logout — values in MRO for DeviationService & storage. */
export function getCashRegisterAmountsFromInputs(
  cashInput,
  electronicInput,
  currencyType = readActiveCurrencyType()
) {
  const cashAmountMro = parseCashRegisterAmountInput(cashInput, currencyType);
  const electronicAmountMro = parseCashRegisterAmountInput(electronicInput, currencyType);
  return {
    cashAmountMro,
    electronicAmountMro,
    totalAmountMro: Math.round((cashAmountMro + electronicAmountMro) * 100) / 100,
  };
}

/** Checkout / sales: both amounts stored in MRO base currency. */
export function validateStoredMroCashPayment(totalMro, cashMro) {
  const total = Number(totalMro) || 0;
  const cash = Number(cashMro) || 0;
  const isValid = cash >= total;

  return {
    isValid,
    total,
    cash,
    changeMro: isValid ? Math.round((cash - total) * 100) / 100 : 0,
    shortfallMro: !isValid ? Math.round((total - cash) * 100) / 100 : 0
  };
}
